<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useRouteQuery } from '@vueuse/router';

import InputText from 'primevue/inputtext';
import Chips from 'primevue/chips';
import Tag from 'primevue/tag';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

import useDataTableDefaults from '@/composables/useDataTableDefaults';
import useInlineRowEdits from '@/composables/useInlineRowEdits';

import RowMenu from '@/components/ListViewComponents/RowMenu.vue';
import DataTableHeader from '@/components/ListViewComponents/DataTableHeader.vue';

import * as Polls from '@/services/polls';

const tableDefaults = useDataTableDefaults();
const $router = useRouter();

const {
  baseDataTableProps,
  tagsProps,
  makeFormattedDate,
  handleSort,
} = tableDefaults;

const { onRowClick } = useInlineRowEdits();

const selectedRows = ref();
const rowsPerPage = ref(10);
const currentPage = useRouteQuery('page', '1', { transform: Number });
const firstPage = computed(() => (currentPage.value - 1) * rowsPerPage.value);

const selectedTags = useRouteQuery('tags', [], {
  transform: (val) => (Array.isArray(val) ? val : [val]),
});

const searchStr = useRouteQuery('search', '');
const sortBy = useRouteQuery('sortBy', '');
const sortDir = useRouteQuery('sortDir', '');

const pollsQuery = computed(() => ({
  page: currentPage.value,
  search: searchStr.value,
  sortBy: sortBy.value,
  sortDir: sortDir.value,
}));

const {
  data: pollsData,
  isFetching: isFetchingPolls,
  execute: fetchPolls,
} = Polls.getPolls(
  pollsQuery,
  { immediate: true, refetch: true },
);

const totalPolls = computed(() => pollsData.value?.total ?? 0);
const users = computed(() => pollsData.value?.users ?? {});

const polls = computed(() => (
  (pollsData.value?.polls ?? []).map((poll) => ({
    ...poll,
    updatedBy: users.value[poll.updatedBy]?.displayName ?? 'Unknown',
    updatedAt: makeFormattedDate(poll.updatedAt),
  }))
));

const pollsColumns = ref([
  {
    field: 'title',
    header: 'Title',
    sortable: true,
  },
  {
    field: 'tags',
    header: 'Tags',
    customBody: true,
  },
  {
    field: 'updatedAt',
    header: 'Modified Date',
    sortable: true,
  },
  {
    field: 'updatedBy',
    header: 'Modified By',
    sortable: true,
  },
  {
    contextualMenu: true,
    align: 'right',
  },
]);

const makeRowHref = (e) => `${window.location.origin}/analytics/poll/${e.data.pollId}`;

const onClickPollRow = (e) => {
  const { pollId } = e.data;

  $router.push({
    name: 'poll-analytics',
    params: { id: pollId },
  });
};

const disableActions = ref(false);
const contextualMenus = ref([]);

const isRunningBulkAction = ref(false);
const bulkDelete = async (onSuccess) => {
  isRunningBulkAction.value = true;

  const allDeletePromises = selectedRows.value.map((item) => Polls.deletePoll(item.pollId, {
    afterFetch() {
      onSuccess(item.title);
    },
  }));

  await Promise.allSettled(allDeletePromises);

  selectedRows.value = [];
  isRunningBulkAction.value = false;
  fetchPolls();
};

const sortTable = (event) => {
  currentPage.value = 1;
  const { field, order } = handleSort(event);
  sortBy.value = field;
  sortDir.value = order;
};

</script>

<template>
  <div>
    <div class="listPage__header">
      <h2 class="listPage__title">Polls</h2>
    </div>

    <!-- TODO: Finalize this data-test selector -->
    <DataTable
      v-bind="baseDataTableProps"
      :value="polls"
      :rows="rowsPerPage"
      :first="firstPage"
      :total-records="totalPolls"
      :loading="isFetchingPolls || isRunningBulkAction"
      dataKey="pollId"
      rowHover
      removable-sort
      @page="currentPage = $event.page + 1"
      @row-click="onRowClick($event, onClickPollRow)"
      @sort="sortTable"
      pt:root:class="listTable__interactive-polls-analytics"
      pt:root:data-test="interactive-polls-analytics-table"
    >
      <template #header>
        <!-- TODO: Add tag filtering when available -->
        <DataTableHeader
          v-model:selectedTags="selectedTags"
          v-model:searchStr="searchStr"
          :tags="false"
          :isFetchingTags="false"
          :selectedRows="selectedRows"
          :bulkDelete="bulkDelete"
          componentType="poll"
        >
          <template #headerRight>
            &nbsp;
          </template>
        </DataTableHeader>
      </template>

      <template #empty>
        <p>No polls found.</p>
      </template>

      <Column
        v-for="(column, idx) in pollsColumns"
        v-bind="column.props"
        :key="`column-${idx}`"
        :field="column.field"
        :header="column.header"
        :sortable="column.sortable"
      >
        <template
          v-if="column.customBody"
          #body="{ data, field }"
        >
          <template v-if="field === 'tags'">
            <Tag
              v-bind="tagsProps"
              v-for="tag in data.tags"
              :key="tag"
              :value="tag"
            />
          </template>
        </template>

        <template
          v-else-if="column.contextualMenu"
          #body="{ data }"
        >
          <RowMenu
            ref="contextualMenus"
            :isDisabled="disableActions"
            :key="`row-menu-${data.pollId}`"
            :data="data"
            :actions="{
              openInNewTab: {
                url: makeRowHref({ data }),
              },
            }" />
        </template>

        <template
          v-if="column.field === 'title' || column.field === 'tags'"
          #editor="{ data, field }"
        >
          <template v-if="field === 'title'">
            <InputText
              :pt:root="{
                'data-entity': `editTitleInline-${data.pollId}`,
                'data-test': 'edit-title-inline',
              }"
              v-model="data[field]"
              autofocus
            />
          </template>

          <template v-if="field === 'tags'">
            <Chips
              :pt:input="{
                'data-entity': `editTagsInline-${data.pollId}`,
                'data-test': 'edit-tags-inline',
              }"
              v-model="data[field]"
              autofocus
              add-on-blur
            />
          </template>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<style lang="scss">
  .listTable__interactive-polls-analytics {
    .listTable__row td {
      &:last-child {
        text-align: right;
      }
    }
  }
</style>
